import { useEffect, useRef } from 'react';

/**
 * AmbientSound — a quiet procedural drone (two soft sine partials through a
 * lowpass filter). No samples, no meditation cliché, no spiritual textures.
 *
 * Sound is OFF by default. When `enabled` is true, we fade in. When false, we
 * fade out and tear down. Unmounts cleanly.
 */
export default function AmbientSound({ enabled }) {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = new AudioCtx();
    ctxRef.current = ctx;

    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 520;
    filter.Q.value = 0.5;
    filter.connect(master);

    // Two slow-detuned partials create a soft, breathing drone
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 110;
    const g1 = ctx.createGain();
    g1.gain.value = 0.18;
    osc1.connect(g1).connect(filter);

    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 165.5; // a perfect-fifth-ish, slightly detuned
    const g2 = ctx.createGain();
    g2.gain.value = 0.12;
    osc2.connect(g2).connect(filter);

    // Very slow LFO on filter cutoff to mimic an inhale/exhale shimmer
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.07; // ~14s cycle
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 80;
    lfo.connect(lfoGain).connect(filter.frequency);

    osc1.start();
    osc2.start();
    lfo.start();

    // Fade in
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(0.12, now + 2.5);

    nodesRef.current = { master, osc1, osc2, lfo };

    // Some browsers require resume on a user gesture
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    return () => {
      cancelled = true;
      const n = nodesRef.current;
      if (!n) return;
      // Audio nodes throw InvalidStateError if stopped/closed twice. These
      // catches are intentional — there's nothing meaningful to recover from.
      try {
        const end = ctx.currentTime + 1.2;
        n.master.gain.cancelScheduledValues(ctx.currentTime);
        n.master.gain.linearRampToValueAtTime(0, end);
        setTimeout(() => {
          try { n.osc1.stop(); n.osc2.stop(); n.lfo.stop(); } catch (err) { /* already stopped */ }
          try { ctx.close(); } catch (err) { /* already closed */ }
        }, 1400);
      } catch (err) {
        if (typeof console !== 'undefined') console.debug('ambient teardown', err);
      }
      nodesRef.current = null;
    };
  }, [enabled]);

  return null;
}
